import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import { isOrgAppearanceFeatureEnabled } from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const ORG_ID_PATTERN = /^[A-Za-z0-9._:@-]{1,128}$/;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const HEX_PATTERN = /^#[0-9A-F]{6}$/;

export class OrganisationAppearanceServiceError extends Error {
  constructor(
    code = "unknown",
    message = "Organisation appearance is unavailable."
  ) {
    super(message);
    this.name = "OrganisationAppearanceServiceError";
    this.code = code;
  }
}

const fail = (code, message) => {
  throw new OrganisationAppearanceServiceError(code, message);
};

const requireActor = (authInstance) => {
  const actor = authInstance?.currentUser;
  if (
    !actor ||
    typeof actor.uid !== "string" ||
    actor.uid.length < 1 ||
    actor.uid.length > 128
  ) {
    fail("unauthenticated", "Sign in again to continue.");
  }
  return actor;
};

const assertCurrentActor = (authInstance, expectedActor) => {
  if (authInstance?.currentUser !== expectedActor) {
    fail(
      "stale-auth-context",
      "The signed-in account changed during the appearance operation."
    );
  }
};

const normalizedOrgId = (value) => {
  const orgId = typeof value === "string" ? value.trim() : "";
  if (!ORG_ID_PATTERN.test(orgId))
    fail("invalid-argument", "Invalid organisation.");
  return orgId;
};

const normalizedSeed = (value, { nullable = false } = {}) => {
  if (nullable && value === null) return null;
  const seed = typeof value === "string" ? value.trim().toUpperCase() : "";
  if (!HEX_PATTERN.test(seed))
    fail("invalid-argument", "Enter a six-digit hex colour.");
  return seed;
};

const validateLayer = (value) => {
  if (
    !value ||
    !["active", "reset"].includes(value.state) ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    (value.state === "active" && !HEX_PATTERN.test(value.accentSeed)) ||
    (value.state === "reset" && value.accentSeed !== null)
  ) {
    fail("invalid-response", "The appearance response was invalid.");
  }
  return Object.freeze({
    state: value.state,
    revision: value.revision,
    accentSeed: value.accentSeed,
  });
};

const validateAppearanceResponse = (value, orgId) => {
  const source = value?.effective?.source;
  const accentSeed = value?.effective?.accentSeed;
  if (
    !value ||
    value.schemaVersion !== 1 ||
    value.paletteVersion !== 1 ||
    value.orgId !== orgId ||
    !value.layers ||
    !["personal", "organisation", "unit"].includes(source) ||
    value.effective?.paletteVersion !== 1 ||
    !value.capabilities ||
    typeof value.capabilities.canManageOrganisationAppearance !== "boolean" ||
    !HEX_PATTERN.test(accentSeed)
  ) {
    fail("invalid-response", "The appearance response was invalid.");
  }
  return Object.freeze({
    schemaVersion: 1,
    paletteVersion: 1,
    orgId,
    layers: Object.freeze({
      personal: validateLayer(value.layers.personal),
      organisation: validateLayer(value.layers.organisation),
    }),
    effective: Object.freeze({ source, accentSeed }),
    capabilities: Object.freeze({
      canManageOrganisationAppearance:
        value.capabilities.canManageOrganisationAppearance,
    }),
  });
};

const serviceError = (error) => {
  if (error instanceof OrganisationAppearanceServiceError) return error;
  const code = String(error?.code || "unknown")
    .split("/")
    .pop();
  const messages = {
    aborted:
      "This appearance changed elsewhere. Load the latest version and try again.",
    "permission-denied": "You do not have access to this appearance.",
    "stale-auth-context":
      "The signed-in account changed. Load this appearance again.",
    unauthenticated: "Sign in again to continue.",
    unavailable: "Appearance settings are temporarily unavailable.",
  };
  return new OrganisationAppearanceServiceError(
    code,
    messages[code] || "Organisation appearance could not be updated."
  );
};

export function createAppearanceOperationId(
  cryptoProvider = globalThis.crypto
) {
  if (typeof cryptoProvider?.randomUUID === "function") {
    return cryptoProvider.randomUUID().replaceAll("-", "_");
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `appearance_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("")}`;
  }
  throw new OrganisationAppearanceServiceError(
    "failed-precondition",
    "Secure appearance operations are unavailable."
  );
}

const appearanceIntentKey = ({
  orgId,
  scope,
  action,
  expectedRevision,
  accentSeed,
}) =>
  JSON.stringify([
    orgId,
    scope,
    action,
    expectedRevision,
    action === "set" ? normalizedSeed(accentSeed) : null,
  ]);

export function createAppearanceOperationTracker({
  operationIdFactory = createAppearanceOperationId,
} = {}) {
  let activeIntentKey = null;
  let activeOperationId = null;

  return Object.freeze({
    operationIdFor(intent) {
      const nextIntentKey = appearanceIntentKey(intent);
      if (nextIntentKey !== activeIntentKey || !activeOperationId) {
        activeIntentKey = nextIntentKey;
        activeOperationId = operationIdFactory();
      }
      return activeOperationId;
    },
    complete(intent) {
      if (appearanceIntentKey(intent) !== activeIntentKey) return;
      activeIntentKey = null;
      activeOperationId = null;
    },
    clear() {
      activeIntentKey = null;
      activeOperationId = null;
    },
  });
}

export function createOrganisationAppearanceService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  enabled = isOrgAppearanceFeatureEnabled,
} = {}) {
  let getCallable;
  let setCallable;

  return Object.freeze({
    async getOwnOrganisationAppearance(orgIdValue) {
      if (!enabled)
        fail("feature-disabled", "Appearance settings are not enabled.");
      const orgId = normalizedOrgId(orgIdValue);
      const expectedActor = requireActor(authInstance);
      if (!getCallable) {
        getCallable = callableFactory(
          functionsInstance,
          "getOwnOrganisationAppearance"
        );
      }
      try {
        const response = await withUiDeadline(
          getCallable({ schemaVersion: 1, orgId }),
          { phase: "organisation appearance" }
        );
        assertCurrentActor(authInstance, expectedActor);
        return validateAppearanceResponse(response?.data, orgId);
      } catch (error) {
        throw serviceError(error);
      }
    },

    async setOwnOrganisationAppearance({
      operationId = createAppearanceOperationId(),
      orgId: orgIdValue,
      scope,
      action,
      expectedRevision,
      accentSeed,
    }) {
      if (!enabled)
        fail("feature-disabled", "Appearance settings are not enabled.");
      const orgId = normalizedOrgId(orgIdValue);
      const expectedActor = requireActor(authInstance);
      if (!OPERATION_ID_PATTERN.test(operationId)) {
        fail("invalid-argument", "Invalid appearance operation.");
      }
      if (!["personal", "organisation"].includes(scope)) {
        fail("invalid-argument", "Invalid appearance scope.");
      }
      if (!["set", "reset"].includes(action)) {
        fail("invalid-argument", "Invalid appearance action.");
      }
      if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) {
        fail("invalid-argument", "Invalid appearance revision.");
      }
      const seed =
        action === "set"
          ? normalizedSeed(accentSeed)
          : accentSeed === null
          ? null
          : fail("invalid-argument", "Reset must not include a colour.");
      if (!setCallable) {
        setCallable = callableFactory(
          functionsInstance,
          "setOwnOrganisationAppearance"
        );
      }
      try {
        const response = await withUiDeadline(
          setCallable({
            schemaVersion: 1,
            operationId,
            orgId,
            scope,
            action,
            expectedRevision,
            accentSeed: seed,
          }),
          { phase: "saving organisation appearance" }
        );
        assertCurrentActor(authInstance, expectedActor);
        const value = response?.data;
        const appearance = validateAppearanceResponse(value, orgId);
        if (
          value.scope !== scope ||
          !["updated", "unchanged", "replayed"].includes(value.outcome) ||
          !Number.isSafeInteger(value.revision) ||
          value.revision < 0 ||
          value.revision !== appearance.layers[scope].revision ||
          (value.outcome === "replayed" &&
            !["updated", "unchanged"].includes(value.originalOutcome))
        ) {
          fail(
            "invalid-response",
            "The saved appearance response was invalid."
          );
        }
        return Object.freeze({
          ...appearance,
          scope,
          layer: appearance.layers[scope],
          outcome: value.outcome,
          originalOutcome:
            value.outcome === "replayed" ? value.originalOutcome : null,
          replayed: value.outcome === "replayed",
          revision: value.revision,
        });
      } catch (error) {
        throw serviceError(error);
      }
    },
  });
}

export const organisationAppearanceService =
  createOrganisationAppearanceService();
